import React, { useState, useEffect, useRef } from "react";
import { Card, CardBody, Col, Button, Spinner, Container, Input, Modal, Nav, NavItem, NavLink, TabContent, TabPane, Badge, Table, Pagination, PaginationItem, PaginationLink } from "reactstrap";
import DataTable from "react-data-table-component";
import Breadcrumbs from "../../CommonElements/Breadcrumbs";
import { apiPost } from "../../Utils";
import { ToastContainer, toast } from "react-toastify";
import { Btn, H3 } from "../../AbstractElements";
import { useNavigate } from "react-router-dom";
import { Edit, Plus, Trash2, Eye, Shield, Lock, CheckCircle, XCircle, Minus } from 'react-feather';
import Swal from 'sweetalert2';
import { useAuth } from "../../Context/AuthContext";

export default function ViewRoles() {
    const [data, setData] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [sortColumn, setSortColumn] = useState("");
    const [sortDirection, setSortDirection] = useState("");

    // Modal State
    const [viewModal, setViewModal] = useState(false);
    const [isClosing, setIsClosing] = useState(false);
    const [selectedRole, setSelectedRole] = useState<any>(null);
    const [activeTab, setActiveTab] = useState('1');
    const [allPermissions, setAllPermissions] = useState<any>({});
    const [modalPage, setModalPage] = useState(1);
    const [modalPerPage] = useState(8);

    const navigate = useNavigate();
    const { hasPermission } = useAuth();
    const prevSearch = useRef(searchTerm);

    useEffect(() => {
        const isSearchChanged = prevSearch.current !== searchTerm;
        const delay = isSearchChanged ? 500 : 0;
        prevSearch.current = searchTerm;

        const timer = setTimeout(() => {
            fetchData(searchTerm, sortColumn, sortDirection);
        }, delay);
        return () => clearTimeout(timer);
    }, [searchTerm, sortColumn, sortDirection]);

    useEffect(() => {
        fetchAllPermissions();
    }, []);

    const fetchAllPermissions = async () => {
        try {
            const response: any = await apiPost("roles/permissions", {});
            setAllPermissions(response);
        } catch (error) {
            console.error("Error fetching permissions:", error);
        }
    };

    const fetchData = async (search: string = "", sortCol: string = "", sortDir: string = "") => {
        setLoading(true);
        try {
            const body: any = {
                search: search
            };
            if (sortCol) {
                body.sort_by = sortCol;
                body.sort_direction = sortDir;
            }

            const response: any = await apiPost("roles/getall", body);
            // Filter out Admin role from the list if not handled by backend
            const filteredRoles = Array.isArray(response)
                ? response.filter((role: any) => role.name !== 'Admin')
                : response;

            if (response && response.data && Array.isArray(response.data)) {
                setData(response.data.filter((role: any) => role.name !== 'Admin'));
            } else {
                setData(filteredRoles);
            }

        } catch (error) {
            console.error("Error fetching data:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleSort = (column: any, sortDir: string) => {
        if (column.sortField) {
            setSortColumn(column.sortField);
            setSortDirection(sortDir);
        }
    };

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(e.target.value);
    };

    const handleAdd = () => {
        navigate(`${process.env.PUBLIC_URL}/add_role`);
    };

    const handleEdit = (id: number) => {
        navigate(`${process.env.PUBLIC_URL}/edit_role`, { state: { id: id } });
    };

    const handleDelete = async (id: number) => {
        Swal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!'
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    await apiPost("roles/delete", { id: id });
                    toast.success("Role deleted successfully", { autoClose: 1000 });
                    fetchData(searchTerm, sortColumn, sortDirection);
                } catch (error) {
                    toast.error("Failed to delete role", { autoClose: 1000 });
                }
            }
        });
    };

    // Modal Helpers
    const toggleViewModal = () => {
        if (viewModal) {
            handleClose();
        } else {
            setViewModal(true);
        }
    };

    const handleClose = () => {
        setIsClosing(true);
        setTimeout(() => {
            setViewModal(false);
            setIsClosing(false);
        }, 550);
    };

    const toggleTab = (tab: string) => {
        if (activeTab !== tab) setActiveTab(tab);
    }

    const handleView = (row: any) => {
        setSelectedRole(row);
        setModalPage(1);
        setActiveTab('1');
        setViewModal(true);
    };

    // Helper to check if permission exists for module/action pair
    const getPermissionId = (module: string, action: string) => {
        const modulePerms = allPermissions[module];
        if (!modulePerms) return null;
        const perm = modulePerms.find((p: any) => p.action === action);
        return perm ? perm.id : null;
    };

    // Helper to check if role has the permission
    const hasRolePermission = (permId: number) => {
        if (!selectedRole || !selectedRole.permissions) return false;
        return selectedRole.permissions.some((p: any) => p.id === permId);
    };

    const columns = [
        {
            name: "Sl.No",
            selector: (row: any, index?: number) => (index ?? 0) + 1,
            sortable: false,
            // width: "150px",
        },
        {
            name: "Role Name",
            selector: (row: any) => row.name,
            sortable: true,
            sortField: "name",
            cell: (row: any) => <span className="fw-bold text-primary">{row.name}</span>
        },
        {
            name: "Permissions Count",
            selector: (row: any) => row.permissions ? row.permissions.length : 0,
            sortable: false,
            cell: (row: any) => <Badge color="info" pill>{row.permissions ? row.permissions.length : 0}</Badge>
        },
        {
            name: "Actions",
            cell: (row: any) => (
                <div className="d-flex justify-content-between gap-2">
                    {hasPermission('role-management.view') && (
                        <Button size="sm" color="primary" onClick={() => handleView(row)} title="View Details">
                            <Eye size={14} />
                        </Button>
                    )}
                    {hasPermission('role-management.edit') && (
                        <Button size="sm" color="info" onClick={() => handleEdit(row.id)} title="Edit">
                            <Edit size={14} />
                        </Button>
                    )}
                    {hasPermission('role-management.delete') && (
                        <Button size="sm" color="danger" onClick={() => handleDelete(row.id)} title="Delete">
                            <Trash2 size={14} />
                        </Button>
                    )}
                </div>
            ),
            ignoreRowClick: true,
            allowOverflow: true,
            button: true,
            width: "160px"
        },
    ];

    const customStyles = {
        headRow: {
            style: {
                backgroundColor: '#e0f2f1',
                color: '#00695c',
                minHeight: '50px',
                borderBottom: '1px solid #e0e0e0',
                fontWeight: 'bold',
                fontSize: '14px',
            },
        },
        headCells: {
            style: {
                fontSize: '15px',
                fontWeight: '600',
                paddingLeft: '16px',
                paddingRight: '16px',
            },
        },
        rows: {
            style: {
                minHeight: '50px',
                '&:not(:last-of-type)': {
                    borderBottomStyle: 'solid',
                    borderBottomWidth: '1px',
                    borderBottomColor: '#e0e0e0',
                },
            },
        },
    };

    const actionColumns = ['view', 'add', 'edit', 'delete', 'transfer', 'approve', 'download', 'print', 'invoice'];

    return (
        <div className='page-body'>
            <Breadcrumbs pageTitle="Role Management" parent="Master Settings" title="Role Management" />
            <Container fluid>
                <Col xl={12} className="mb-lg-3">
                    <Card>
                        <CardBody>
                            <Col xs={12} className="mt-2 d-flex justify-content-between align-items-center mb-3">
                                <H3 className="text-primary">Roles Overview</H3>
                                {hasPermission('role-management.add') && (
                                    <Btn color="primary" onClick={handleAdd}>
                                        <Plus size={16} /> Add Role
                                    </Btn>
                                )}
                            </Col>
                            {hasPermission('role-management.view') && (
                                <>
                                    <Col xs={12} className="mb-3 d-flex justify-content-end">
                                        <Input type="text" placeholder="Search..." value={searchTerm} onChange={handleSearchChange} style={{ maxWidth: "300px" }} />
                                    </Col>
                                    <div className="table-responsive">
                                        <DataTable
                                            data={data}
                                            columns={columns}
                                            pagination
                                            progressPending={loading}
                                            progressComponent={<div className="d-flex justify-content-center align-items-center w-100" style={{ minHeight: '50vh' }}><Spinner color="primary" /></div>}
                                            highlightOnHover
                                            sortServer
                                            onSort={handleSort}
                                            customStyles={customStyles}
                                        />
                                    </div>
                                </>
                            )}
                        </CardBody>
                    </Card>
                </Col>
            </Container>

            {/* View Role Modal */}
            <Modal isOpen={viewModal} toggle={toggleViewModal} size="xl" centered contentClassName={isClosing ? "animate-3d-slit-out" : "animate-3d-slit"} fade={false}>
                {selectedRole && (
                    <div style={{
                        background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)', // Indigo/Purple gradient
                        padding: '1.5rem',
                        position: 'relative', width: '100%'
                    }}>
                        <button
                            className="btn-close btn-close-white"
                            type="button"
                            onClick={handleClose}
                            style={{ position: 'absolute', top: '1rem', right: '1rem', opacity: 0.9 }}
                        ></button>

                        <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem' }}>
                            <div style={{
                                width: '60px',
                                height: '60px',
                                borderRadius: '50%',
                                backgroundColor: 'rgba(255,255,255,0.2)',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                flexShrink: 0
                            }}>
                                <Shield size={30} color="white" />
                            </div>
                            <div style={{ flex: 1 }}>
                                <h3 style={{
                                    color: 'white',
                                    fontWeight: 700,
                                    fontSize: '1.5rem',
                                    margin: 0,
                                    marginBottom: '0.25rem'
                                }}>
                                    {selectedRole.name}
                                </h3>
                                <div style={{ display: 'flex', gap: '1rem', color: 'rgba(255,255,255,0.85)', fontSize: '0.9rem' }}>
                                    <span style={{ fontWeight: 600 }}>Active Role</span>
                                </div>
                            </div>
                            <div>
                                <span style={{
                                    backgroundColor: 'rgba(255,255,255,0.2)',
                                    color: 'white',
                                    padding: '0.5rem 1rem',
                                    margin: '0.5rem 1rem',
                                    borderRadius: '20px',
                                    fontSize: '1rem',
                                    fontWeight: 700,
                                    border: '1px solid rgba(255,255,255,0.2)',
                                    backdropFilter: 'blur(4px)'
                                }}>
                                    {selectedRole.permissions ? selectedRole.permissions.length : 0} Permissions
                                </span>
                            </div>
                        </div>
                    </div>
                )}

                <div className="modal-body p-0">
                    <Nav tabs className="nav-primary" style={{ padding: '0 1.5rem', marginTop: '1rem', borderBottom: '1px solid #dee2e6' }}>
                        <NavItem>
                            <NavLink
                                className={activeTab === '1' ? 'active' : ''}
                                onClick={() => toggleTab('1')}
                                style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 20px' }}
                            >
                                <Lock size={16} /> Permissions Matrix
                            </NavLink>
                        </NavItem>
                    </Nav>

                    <div className="p-0" style={{ backgroundColor: '#f8f9fa', minHeight: '300px' }}>
                        {selectedRole && (
                            <TabContent activeTab={activeTab}>
                                <TabPane tabId="1">
                                    <div className="bg-white p-3 rounded shadow-sm">
                                        {Object.keys(allPermissions).length > 0 ? (
                                            <>
                                                <div className="table-responsive">
                                                    <Table bordered hover striped className="mb-0">
                                                        <thead className="table-light">
                                                            <tr>
                                                                <th style={{ minWidth: '150px' }} className="fw-bold text-dark">Module</th>
                                                                {actionColumns.map(action => (
                                                                    <th key={action} className="text-center text-capitalize fw-bold text-dark">
                                                                        {action}
                                                                    </th>
                                                                ))}
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {Object.keys(allPermissions).sort()
                                                                .slice((modalPage - 1) * modalPerPage, modalPage * modalPerPage)
                                                                .map((module) => (
                                                                    <tr key={module}>
                                                                        <td className="fw-bold text-dark text-capitalize">{module.replace(/[-_]/g, ' ')}</td>
                                                                        {actionColumns.map(action => {
                                                                            const permId = getPermissionId(module, action);
                                                                            const isAssigned = permId ? hasRolePermission(permId) : false;

                                                                            return (
                                                                                <td key={action} className="text-center">
                                                                                    {permId ? (
                                                                                        isAssigned ? (
                                                                                            <CheckCircle size={18} className="text-success" />
                                                                                        ) : (
                                                                                            <span className="text-muted opacity-25" style={{ fontSize: '18px' }}>-</span>
                                                                                        )
                                                                                    ) : (
                                                                                        <span className="text-muted opacity-10">
                                                                                            <Minus size={14} />
                                                                                        </span>
                                                                                    )}
                                                                                </td>
                                                                            );
                                                                        })}
                                                                    </tr>
                                                                ))}
                                                        </tbody>
                                                    </Table>
                                                </div>
                                                {Object.keys(allPermissions).length > modalPerPage && (
                                                    <div className="d-flex justify-content-center mt-3">
                                                        <Pagination aria-label="Page navigation">
                                                            <PaginationItem disabled={modalPage <= 1}>
                                                                <PaginationLink previous onClick={() => setModalPage(modalPage - 1)} />
                                                            </PaginationItem>
                                                            {[...Array(Math.ceil(Object.keys(allPermissions).length / modalPerPage))].map((_, i) => (
                                                                <PaginationItem active={i + 1 === modalPage} key={i}>
                                                                    <PaginationLink onClick={() => setModalPage(i + 1)}>
                                                                        {i + 1}
                                                                    </PaginationLink>
                                                                </PaginationItem>
                                                            ))}
                                                            <PaginationItem disabled={modalPage >= Math.ceil(Object.keys(allPermissions).length / modalPerPage)}>
                                                                <PaginationLink next onClick={() => setModalPage(modalPage + 1)} />
                                                            </PaginationItem>
                                                        </Pagination>
                                                    </div>
                                                )}
                                            </>
                                        ) : (
                                            <div className="text-center py-5 text-muted">
                                                <Shield size={40} className="mb-3 opacity-25" />
                                                <p>Loading permissions or none available...</p>
                                            </div>
                                        )}
                                    </div>
                                </TabPane>
                            </TabContent>
                        )}
                    </div>
                </div>
            </Modal>
            <ToastContainer />
        </div>
    );
}
